// Network detection and retry mechanisms
import { errorHandler } from './errorHandler';

export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffFactor: number;
  retryCondition?: (error: any) => boolean;
}

export interface NetworkStatus {
  isOnline: boolean;
  connectionType?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

export class NetworkManager {
  private static instance: NetworkManager;
  private networkStatus: NetworkStatus = { isOnline: navigator.onLine };
  private listeners: Set<(status: NetworkStatus) => void> = new Set();
  private retryQueues: Map<string, Array<() => Promise<any>>> = new Map();

  static getInstance(): NetworkManager {
    if (!NetworkManager.instance) {
      NetworkManager.instance = new NetworkManager();
    }
    return NetworkManager.instance;
  }

  constructor() {
    this.setupNetworkListeners();
    this.updateNetworkStatus();
  }

  /**
   * Set up network event listeners
   */
  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      this.updateNetworkStatus();
      this.processRetryQueues();
    });

    window.addEventListener('offline', () => {
      this.updateNetworkStatus();
    });

    // Listen for connection changes if supported
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      connection.addEventListener('change', () => {
        this.updateNetworkStatus();
      });
    }
  }

  /**
   * Update current network status
   */
  private updateNetworkStatus(): void {
    const prevStatus = { ...this.networkStatus };
    
    this.networkStatus.isOnline = navigator.onLine;

    // Get connection info if available
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      this.networkStatus.connectionType = connection.type;
      this.networkStatus.effectiveType = connection.effectiveType;
      this.networkStatus.downlink = connection.downlink;
      this.networkStatus.rtt = connection.rtt;
    }

    // Notify listeners if status changed
    if (prevStatus.isOnline !== this.networkStatus.isOnline) {
      this.notifyListeners();
    }
  }

  /**
   * Get current network status
   */
  getNetworkStatus(): NetworkStatus {
    return { ...this.networkStatus };
  }

  /**
   * Check if network is available with connectivity test
   */
  async checkConnectivity(): Promise<boolean> {
    if (!navigator.onLine) {
      return false;
    }

    try {
      const isConnected = await errorHandler.checkNetworkConnectivity();
      this.networkStatus.isOnline = isConnected;
      return isConnected;
    } catch {
      this.networkStatus.isOnline = false;
      return false;
    }
  }

  /**
   * Subscribe to network status changes
   */
  subscribe(listener: (status: NetworkStatus) => void): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of status change
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.networkStatus);
      } catch (error) {
        console.error('Error in network status listener:', error);
      }
    });
  }

  /**
   * Retry function with exponential backoff
   */
  async retryWithBackoff<T>(
    fn: () => Promise<T>,
    config: Partial<RetryConfig> = {}
  ): Promise<T> {
    const {
      maxAttempts = 3,
      baseDelay = 1000,
      maxDelay = 10000,
      backoffFactor = 2,
      retryCondition = this.defaultRetryCondition,
    } = config;

    let lastError: any;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Check network connectivity before retry
        if (attempt > 1) {
          const isConnected = await this.checkConnectivity();
          if (!isConnected) {
            throw new Error('Network not available');
          }
        }

        return await fn();
      } catch (error) {
        lastError = error;
        
        // Don't retry if condition is not met or it's the last attempt
        if (!retryCondition(error) || attempt === maxAttempts) {
          throw error;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          baseDelay * Math.pow(backoffFactor, attempt - 1),
          maxDelay
        );

        // Add jitter to prevent thundering herd
        const jitteredDelay = delay + Math.random() * 1000;

        console.log(`Retry attempt ${attempt}/${maxAttempts} in ${jitteredDelay}ms`);
        await this.delay(jitteredDelay);
      }
    }

    throw lastError;
  }

  /**
   * Default retry condition - retry on network errors
   */
  private defaultRetryCondition(error: any): boolean {
    // Retry on network errors, timeouts, and 5xx server errors
    return (
      error.name === 'NetworkError' ||
      error.code === 'NETWORK_ERROR' ||
      error.message?.includes('timeout') ||
      error.message?.includes('fetch') ||
      (error.status >= 500 && error.status < 600) ||
      error.status === 0 // Network failure
    );
  }

  /**
   * Queue operation for retry when network comes back online
   */
  queueForRetry(key: string, operation: () => Promise<any>): void {
    if (!this.retryQueues.has(key)) {
      this.retryQueues.set(key, []);
    }
    
    this.retryQueues.get(key)!.push(operation);
  }

  /**
   * Process all queued operations when network comes back
   */
  private async processRetryQueues(): Promise<void> {
    if (!this.networkStatus.isOnline) {
      return;
    }

    for (const [key, operations] of Array.from(this.retryQueues.entries())) {
      console.log(`Processing ${operations.length} queued operations for ${key}`);
      
      // Process operations sequentially to avoid overwhelming the network
      for (const operation of operations) {
        try {
          await operation();
        } catch (error) {
          console.error(`Failed to process queued operation for ${key}:`, error);
        }
      }
      
      // Clear the queue after processing
      this.retryQueues.delete(key);
    }
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Create a network-aware fetch wrapper
   */
  async networkAwareFetch(
    url: string, 
    options: RequestInit = {},
    retryConfig?: Partial<RetryConfig>
  ): Promise<Response> {
    return this.retryWithBackoff(async () => {
      // Check network before making request
      const isConnected = await this.checkConnectivity();
      if (!isConnected) {
        throw new Error('Network not available');
      }

      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    }, retryConfig);
  }

  /**
   * Get network quality indicator
   */
  getNetworkQuality(): 'excellent' | 'good' | 'fair' | 'poor' | 'offline' {
    if (!this.networkStatus.isOnline) {
      return 'offline';
    }

    const { effectiveType, rtt, downlink } = this.networkStatus;

    // Use effective connection type if available
    if (effectiveType) {
      switch (effectiveType) {
        case '4g':
          return 'excellent';
        case '3g':
          return 'good';
        case '2g':
          return 'fair';
        case 'slow-2g':
          return 'poor';
      }
    }

    // Fallback to RTT and downlink if available
    if (rtt !== undefined && downlink !== undefined) {
      if (rtt < 100 && downlink > 5) {
        return 'excellent';
      } else if (rtt < 300 && downlink > 1.5) {
        return 'good';
      } else if (rtt < 600 && downlink > 0.5) {
        return 'fair';
      } else {
        return 'poor';
      }
    }

    // Default to good if we can't determine quality
    return 'good';
  }
}

// Export singleton instance
export const networkManager = NetworkManager.getInstance();

// Utility functions
export const retryWithBackoff = <T>(
  fn: () => Promise<T>,
  config?: Partial<RetryConfig>
): Promise<T> => {
  return networkManager.retryWithBackoff(fn, config);
};

export const networkAwareFetch = (
  url: string,
  options?: RequestInit,
  retryConfig?: Partial<RetryConfig>
): Promise<Response> => {
  return networkManager.networkAwareFetch(url, options, retryConfig);
};

export const getNetworkStatus = (): NetworkStatus => {
  return networkManager.getNetworkStatus();
};

export const subscribeToNetworkStatus = (
  listener: (status: NetworkStatus) => void
): (() => void) => {
  return networkManager.subscribe(listener);
};